import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL platform users
export const fetchAllPlatformUsers = createAsyncThunk(
  "platformUser/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.PLATFORM_USER.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ PlatformUsers API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch platform users",
      );
    }
  },
);

// Thunk for fetching platform user by ID
export const fetchPlatformUserById = createAsyncThunk(
  "platformUser/fetchById",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.PLATFORM_USER.SELECT_BY_ID}/${userId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Platform user not found",
      );
    }
  },
);

// Thunk for adding a new platform user
export const addPlatformUser = createAsyncThunk(
  "platformUser/add",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.PLATFORM_USER.INSERT,
        userData,
      );
      dispatch(fetchAllPlatformUsers());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add platform user",
      );
    }
  },
);

// Thunk for updating an existing platform user
export const updatePlatformUser = createAsyncThunk(
  "platformUser/update",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.PLATFORM_USER.UPDATE,
        userData,
      );
      dispatch(fetchAllPlatformUsers()); // Refresh list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update platform user",
      );
    }
  },
);

// Thunk for deleting a platform user
export const deletePlatformUser = createAsyncThunk(
  "platformUser/delete",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      // Use query parameter if the backend requires it
      await axiosInstance.delete(
        `${API_ENDPOINTS.PLATFORM_USER.DELETE}?id=${userId}`,
      );
      dispatch(fetchAllPlatformUsers());
      return userId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete platform user",
      );
    }
  },
);

const platformUserSlice = createSlice({
  name: "platformUser",
  initialState: {
    usersList: [], // Flat array
    pagination: null,
    selectedUser: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedPlatformUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Users - FIXED
      .addCase(fetchAllPlatformUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllPlatformUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.usersList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllPlatformUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch platform users";
        console.error("❌ PlatformUsers API Failed:", action.payload);
      })
      // Fetch By ID
      .addCase(fetchPlatformUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlatformUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload.data;
      })
      .addCase(fetchPlatformUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add User
      .addCase(addPlatformUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPlatformUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addPlatformUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update User
      .addCase(updatePlatformUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePlatformUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updatePlatformUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete User
      .addCase(deletePlatformUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePlatformUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deletePlatformUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedPlatformUser } = platformUserSlice.actions;
export default platformUserSlice.reducer;
